import { useState } from "react";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import { Reveal } from "@/components/Motion";
import api, { fmtErr } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export default function Contact() {
  usePageMeta({
    title: "Contact the Office | Solis Casting",
    description: "Reach the Solis Casting office — casting inquiries, brand partnerships, press and member support.",
    url: `${window.location.origin}/contact`,
  });
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[85vh] grid grid-cols-1 lg:grid-cols-12" data-testid="contact-page">
      <div className="lg:col-span-6 flex flex-col justify-center px-6 sm:px-10 py-24 border-b lg:border-b-0 lg:border-r border-white/10">
        <Reveal>
          <p className="overline-label mb-8">The Office</p>
          <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter leading-[0.95] mb-12">
            TALK TO <br /><span className="italic text-accent">US.</span>
          </h1>
          <div className="flex flex-col gap-6 max-w-md">
            <p className="text-muted-foreground leading-relaxed">
              Questions about a casting call, your book, a payment, or the platform itself — the office reads everything, usually within a day.
            </p>
            <p className="flex items-center gap-3 font-grotesk text-xs uppercase tracking-[0.15em] text-white/60">
              <MapPin size={14} className="text-accent" /> Advent Studios LLC — New York, NY 10012
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6 flex items-center px-6 sm:px-10 py-24">
        {sent ? (
          <div className="w-full max-w-md mx-auto" data-testid="contact-confirmation">
            <p className="font-display italic text-4xl mb-4">Consider it read.</p>
            <p className="text-muted-foreground leading-relaxed">
              Your message is with the office. Expect a reply at {form.email} — usually within one working day.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md mx-auto" data-testid="contact-form">
            <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Full name" className="field mb-6" />
            <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email" className="field mb-6" />
            <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="How can the office help?" className="field resize-none mb-10" />
            <button data-testid="contact-submit-button" disabled={busy} className="btn-primary w-full disabled:opacity-40">
              {busy ? "Sending…" : "Send to the office"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
